import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Send, CheckCircle, MessageSquare, Compass, Heart, Clock, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsSettings } from '../lib/cms.js';

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [expandedMember, setExpandedMember] = useState(null);
  const settings = useCmsSettings(siteData);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pt-20 pb-24">
      <SEO
        title="Kontakt os | Rådgivning, hjælp og henvendelser"
        description="Har du brug for hjælp, rådgivning eller ønsker du at komme i kontakt med Tak For Din Indsats? Skriv fortroligt til os – vi står klar til at lytte og hjælpe."
      />
      {/* Hero / Header Section */}
      <div className="relative bg-slate-900 text-white py-20 mb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.15),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Kontakt Os
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto font-medium leading-relaxed">
            Vi er her for at hjælpe dig
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 mb-24">
          {/* Info Side */}
          <div className="text-left mt-8">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
              Hvordan kontakter du os?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-12">
              Send os en email, så vender vi tilbage til dig hurtigst muligt
            </p>

            <div className="space-y-8">
              {/* Kom i kontakt */}
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center shrink-0">
                  <Mail size={24} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Kom i kontakt</h3>
                  <p className="text-slate-600 mb-2">Udfyld formularen eller kontakt os direkte.</p>
                  <a href={`mailto:${settings.contact.emails[0] || 'info@takfordinindsats.dk'}`} className="text-base font-bold text-red-600 hover:text-red-700 transition-colors">
                    {settings.contact.emails[0] || 'info@takfordinindsats.dk'}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div id="kontakt-formular" className="bg-white rounded-[2.5rem] p-5 sm:p-8 md:p-12 shadow-2xl shadow-slate-200/60 border border-slate-50 scroll-mt-24">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Tak for din besked!</h3>
                <p className="text-slate-600 mb-8">Vi har modtaget din henvendelse og vender tilbage hurtigst muligt.</p>
                <button 
                  onClick={() => setStatus('idle')}
                  className="px-8 py-3 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition-all"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - HIDDEN */}
                <div className="sr-only absolute -z-10 opacity-0 pointer-events-none">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" name="website_url" id="website_url" tabIndex="-1" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-1">Navn</label>
                    <input 
                      required 
                      name="name"
                      type="text" 
                      placeholder="Dit fulde navn" 
                      className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-1">E-mail</label>
                    <input 
                      required 
                      name="email"
                      type="email" 
                      placeholder="din@email.dk" 
                      className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Emne</label>
                  <select 
                    name="subject"
                    className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all appearance-none"
                  >
                    <option>Søg hjælp</option>
                    <option>Bliv frivillig</option>
                    <option>Donationer & Sponsorater</option>
                    <option>Andet</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Besked</label>
                  <textarea 
                    required 
                    name="message"
                    rows="5" 
                    placeholder="Hvordan kan vi hjælpe dig?" 
                    className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all resize-none"
                  ></textarea>
                </div>

                <button 
                  disabled={status === 'loading'}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-red-600 text-white rounded-[2rem] font-black text-lg hover:bg-red-700 transition-all shadow-xl shadow-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  {status === 'loading' ? 'Sender...' : 'Send besked'}
                </button>
                
                {status === 'error' && (
                  <p className="text-red-600 text-center font-bold text-sm">
                    Der opstod en fejl. Prøv venligst igen senere.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
